import { useState } from 'react';
import GalleryRowsEditor from './GalleryRowsEditor';

/**
 * UniversalPhotoBlocks
 * --------------------
 * A tab that lets admins add gallery rows to ANY page/section by choosing a
 * "scope" string. Every scope becomes an independent collection of rows on
 * the backend. Public pages mount <GalleryRows scope="page:<slug>" /> to
 * pull in these blocks — so admins add photos to any page without code.
 */

const PRESETS = [
  { scope: 'page:home-bottom', label: 'Home page — bottom' },
  { scope: 'page:about-bottom', label: 'About page — bottom' },
  { scope: 'page:leadership-bottom', label: 'Leadership page — bottom' },
  { scope: 'page:events-bottom', label: 'Events page — bottom' },
  { scope: 'page:contact-bottom', label: 'Contact page — bottom' },
  { scope: 'page:womens-history', label: "Women's History page — inline strip" },
];

export default function UniversalPhotoBlocks() {
  const [scope, setScope] = useState(PRESETS[0].scope);
  const [committedScope, setCommittedScope] = useState(PRESETS[0].scope);
  // Only remount the underlying editor when the admin blurs or clicks Apply,
  // so typing a custom scope string doesn't refetch on every keystroke.
  const apply = () => setCommittedScope(scope);
  return (
    <div>
      <div className="bg-white border rounded p-4 mb-4">
        <div className="text-sm font-bold text-royal mb-2">Universal Photo Blocks</div>
        <div className="text-xs text-charcoal-muted mb-3">
          Add photo rows to any page or section by choosing (or typing) a <em>scope</em>.
          Any page can pull them in with <code>&lt;GalleryRows scope="…"/&gt;</code>. The presets below
          are already wired to their respective pages.
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="text-sm">
            <div className="text-royal font-semibold mb-1">Choose preset location</div>
            <select value={scope} onChange={(e) => { setScope(e.target.value); setCommittedScope(e.target.value); }}
              data-testid="upb-preset-select"
              className="w-full px-3 py-2 border rounded">
              {PRESETS.map(p => <option key={p.scope} value={p.scope}>{p.label}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <div className="text-royal font-semibold mb-1">Or type a custom scope</div>
            <div className="flex gap-2">
              <input value={scope} onChange={(e) => setScope(e.target.value)}
                onBlur={apply}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } }}
                data-testid="upb-scope-input"
                placeholder="page:my-custom-slug"
                className="flex-1 px-3 py-2 border rounded font-mono text-xs" />
              <button onClick={apply} data-testid="upb-scope-apply"
                className="text-xs px-3 py-2 bg-royal text-white rounded">Apply</button>
            </div>
            <div className="text-[11px] text-charcoal-muted mt-1">
              Tip: use <code>page:&lt;slug&gt;</code>. Then wherever you want the photos to appear, drop <code>&lt;GalleryRows scope="page:&lt;slug&gt;"/&gt;</code>.
            </div>
          </label>
        </div>
      </div>
      <GalleryRowsEditor scope={committedScope} title={`Photo Blocks · ${committedScope}`} />
    </div>
  );
}
